// stores/quickAddStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Configuration dictionary with navigation paths
export const QUICK_ADD_CONFIG = {
    sales: {
        label: 'Sales',
        items: {
            salesOrder: {
                label: 'Sales Order',
                path: '/sales/add'
            },
            salesReturn: {
                label: 'Sales Return',
                path: 'sales/return/add'
            },
            customer: {
                label: 'Customer',
                path: 'sales/customers/add'
            },
            priceList: {
                label: 'Pricelist',
                path: 'sales/pricelist/add'
            }
        }
    },
    purchase: {
        label: 'Purchase',
        items: {
            purchaseOrder: {
                label: 'Purchase Order',
                path: 'purchase/add'
            },
            purchaseReturn: {
                label: 'Purchase Return',
                path: 'purchase/return/add'
            },
            vendor: {
                label: 'Vendor',
                path: 'purchase/vendors/add'
            },
        }
    },
    inventory: {
        label: 'Inventory',
        items: {
            variants: {
                label: 'Product',
                path: 'inventory/variants/add'
            },
            product: {
                label: 'Product Group',
                path: 'inventory/products/add'
            },
            productAttributes: {
                label: 'Product Attribute',
                path: 'inventory/product-attributes/add'
            },
            productsCategory: {
                label: 'Product Category',
                path: 'inventory/product-categories/add'
            },
            uoms: {
                label: "UOM's",
                path: 'inventory/uoms/add'
            },
            uomsCategory: {
                label: "UOM Category",
                path: 'inventory/uoms/categories'
            },
            mWarehouse: {
                label: "Mobile Warehouse",
                path: 'inventory/warehouses/mobile/add'
            },
            pWarehouse: {
                label: "Physical Warehouse",
                path: 'inventory/warehouses/physical/add'
            },
            InventoryAdjustments: {
                label: "Inventory Adjustments",
                path: 'inventory/stock/inventory-adjustments/add'
            },
            transfers: {
                label: "Transfer",
                path: 'inventory/stock/transfers/add'
            },
        }
    },
    vehicle: {
        label: 'Vehicle',
        items: {
            vehicle: {
                label: 'Vehicle',
                path: 'vehicle/add'
            },
            maintenance: {
                label: 'Maintenance',
                path: 'vehicle/maintenance/add'
            },
            driver: {
                label: 'Driver',
                path: 'vehicle/drivers/add'
            },
            penalties: {
                label: 'Penalties',
                path: 'vehicle/penalties/add'
            },
            vehicleType: {
                label: 'Vehicle Type',
                path: 'vehicle/type'
            }
        }
    },
    expenses: {
        label: 'Expenses',
        items: {
            expenses: {
                label: 'Expenses',
                path: 'expenses/add'
            },
            purchaseReturn: {
                label: 'Expense Category',
                path: 'expenses/category'
            },
        }
    },
    
};

// Default enabled items
const DEFAULT_CONFIG = {
    sales: {
        enabled: true,
        items: ['salesOrder', 'customer']
    },
    purchase: {
        enabled: true,
        items: ['purchaseOrder', 'purchaseReturn']
    },
    inventory: {
        enabled: true,
        items: ['product','variants',]
    },
};

export const useQuickAddStore = create(
    persist(
        (set, get) => ({
            // State
            enabledConfig: DEFAULT_CONFIG,
            
            // Actions
            setEnabledConfig: (config) => set({ enabledConfig: config }),
            
            // Toggle entire section
            toggleSection: (section, enabled) => {
                const allItems = Object.keys(QUICK_ADD_CONFIG[section].items);
                set(state => ({
                    enabledConfig: {
                        ...state.enabledConfig,
                        [section]: {
                            enabled,
                            items: enabled ? allItems : []
                        }
                    }
                }));
            },
            
            // Toggle individual item
            toggleItem: (section, item, checked) => {
                set(state => {
                    const currentItems = state.enabledConfig[section]?.items || [];
                    let newItems;
                    
                    if (checked) {
                        newItems = [...currentItems, item];
                    } else {
                        newItems = currentItems.filter(i => i !== item);
                    }
                    
                    const sectionEnabled = newItems.length > 0;
                    
                    return {
                        enabledConfig: {
                            ...state.enabledConfig,
                            [section]: {
                                enabled: sectionEnabled,
                                items: newItems
                            }
                        }
                    };
                });
            },
            
            // Reset to defaults
            resetToDefaults: () => set({ enabledConfig: DEFAULT_CONFIG }),
            
            // Computed getters
            getEnabledSections: () => {
                const config = get().enabledConfig;
                return Object.keys(QUICK_ADD_CONFIG).filter(section => 
                    config[section]?.enabled && 
                    (config[section]?.items?.length > 0)
                );
            },
            
            getEnabledItemsForSection: (section) => {
                const config = get().enabledConfig;
                return config[section]?.items || [];
            },
            
            isAllItemsSelected: (section) => {
                const config = get().enabledConfig;
                const allItems = Object.keys(QUICK_ADD_CONFIG[section].items);
                const enabledItems = config[section]?.items || [];
                return allItems.length > 0 && allItems.every(item => enabledItems.includes(item));
            },
            
            isSomeItemsSelected: (section) => {
                const config = get().enabledConfig;
                const enabledItems = config[section]?.items || [];
                return enabledItems.length > 0;
            }
        }),
        {
            name: 'quick-add-storage', // name of the item in the storage (must be unique)
            // You can also add version for migrations if needed
            version: 1,
        }
    )
);